import { useEffect } from 'react';

const MIN_SCREEN_WIDTH = 992;
const TRANSITION_DURATION = '0.3s';
const MAX_RIGHT_POSITION = 200;
const DEFAULT_WIDGET_OFFSET = 24;
const MIN_WIDGET_Z_INDEX = 20;
const CART_OVERLAY_Z_INDEX = 50;
const WIDGET_Z_INDEX = CART_OVERLAY_Z_INDEX + 10;
const CART_SIDEBAR_SELECTOR = '[data-testid="shopping-cart-sidebar"]';
const CART_GAP_PX = 16;
const FULLSCREEN_TOLERANCE_PX = 4;

type ShiftedStyles = {
  transform: string;
  transition: string;
  zIndex: string;
};

const shouldShift = () =>
  typeof window !== 'undefined' && window.innerWidth >= MIN_SCREEN_WIDTH;

const isCartSidebar = (element: HTMLElement) =>
  element.getAttribute('data-testid') === 'shopping-cart-sidebar';

const isFloatingWidget = (
  element: HTMLElement,
  styles: CSSStyleDeclaration,
) => {
  if (styles.position !== 'fixed' && styles.position !== 'absolute') {
    return false;
  }

  if (element.closest('[data-cart-modal="open"]') || isCartSidebar(element)) {
    return false;
  }

  const rightValue = Number.parseInt(styles.right, 10);
  const isRightPositioned =
    !Number.isNaN(rightValue) &&
    rightValue >= 0 &&
    rightValue <= MAX_RIGHT_POSITION;
  const isVisible = element.offsetWidth > 0 && element.offsetHeight > 0;
  const zIndexValue = Number.parseInt(styles.zIndex, 10);
  const hasHighZIndex = !Number.isNaN(zIndexValue)
    ? zIndexValue >= MIN_WIDGET_Z_INDEX
    : styles.zIndex === 'auto';

  return isRightPositioned && isVisible && hasHighZIndex;
};

const snapshotStyles = (element: HTMLElement): ShiftedStyles => ({
  transform: element.style.transform || '',
  transition: element.style.transition || '',
  zIndex: element.style.zIndex || '',
});

const restoreStyles = (element: HTMLElement, styles: ShiftedStyles) => {
  element.style.transform = styles.transform;
  element.style.transition = styles.transition;
  element.style.zIndex = styles.zIndex;
};

const applyShift = (element: HTMLElement, distance: number) => {
  element.style.transition = `transform ${TRANSITION_DURATION} ease-in-out`;
  const translation = Math.max(distance - DEFAULT_WIDGET_OFFSET, 0);
  element.style.transform = `translate3d(-${translation}px, 0, 0)`;

  const currentZIndex = Number.parseInt(
    window.getComputedStyle(element).zIndex,
    10,
  );
  if (!Number.isNaN(currentZIndex) && currentZIndex < WIDGET_Z_INDEX) {
    element.style.zIndex = `${WIDGET_Z_INDEX}`;
  }
};

class FloatingElementsShiftManager {
  private shifted = new Map<HTMLElement, ShiftedStyles>();

  private rafId: number | null = null;

  private getCartSidebarWidth = () => {
    const sidebar = document.querySelector(
      CART_SIDEBAR_SELECTOR,
    ) as HTMLElement | null;

    if (!sidebar) {
      return 0;
    }

    return sidebar.getBoundingClientRect().width;
  };

  private getShiftDistance = () => {
    if (!shouldShift()) {
      return 0;
    }

    const cartWidth = this.getCartSidebarWidth();

    if (!cartWidth) {
      return 0;
    }

    if (cartWidth >= window.innerWidth - FULLSCREEN_TOLERANCE_PX) {
      return 0;
    }

    return cartWidth + CART_GAP_PX;
  };

  scheduleShift = () => {
    if (typeof window === 'undefined') {
      return;
    }

    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
    }

    this.rafId = window.requestAnimationFrame(() => {
      this.rafId = null;
      this.shiftNow();
    });
  };

  handleResize = () => {
    if (!shouldShift()) {
      this.reset();
      return;
    }

    if (this.shifted.size === 0) {
      this.scheduleShift();
    }
  };

  dispose = () => {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    this.reset();
  };

  private shiftNow = () => {
    if (!shouldShift() || !document.body) {
      this.reset();
      return;
    }

    const shiftDistance = this.getShiftDistance();

    if (shiftDistance === 0) {
      this.reset();
      return;
    }

    const bodyChildren = Array.from(document.body.children) as HTMLElement[];
    bodyChildren.forEach((element) =>
      this.shiftElement(element, shiftDistance),
    );
  };

  private shiftElement = (element: HTMLElement, shiftDistance: number) => {
    if (this.shifted.has(element)) {
      return;
    }

    const styles = window.getComputedStyle(element);
    if (!isFloatingWidget(element, styles)) {
      return;
    }

    this.shifted.set(element, snapshotStyles(element));
    applyShift(element, shiftDistance);
  };

  private reset = () => {
    this.shifted.forEach((styles, element) => restoreStyles(element, styles));
    this.shifted.clear();
  };
}

export const useFloatingElementsShift = (isClosing = false) => {
  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      typeof document === 'undefined' ||
      !document.body
    ) {
      return undefined;
    }

    const manager = new FloatingElementsShiftManager();

    manager.scheduleShift();

    const observer = new MutationObserver(() => manager.scheduleShift());

    observer.observe(document.body, {
      childList: true,
      subtree: false,
    });

    const sidebar = document.querySelector(
      CART_SIDEBAR_SELECTOR,
    ) as HTMLElement | null;

    let resizeObserver: ResizeObserver | undefined;
    if (sidebar && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(() => manager.scheduleShift());
      resizeObserver.observe(sidebar);
    }

    window.addEventListener('resize', manager.handleResize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', manager.handleResize);
      resizeObserver?.disconnect();
      manager.dispose();
    };
  }, []);

  useEffect(() => {
    if (!isClosing || typeof document === 'undefined') {
      return;
    }

    const bodyChildren = Array.from(document.body.children) as HTMLElement[];

    bodyChildren.forEach((element) => {
      const styles = window.getComputedStyle(element);
      if (isFloatingWidget(element, styles)) {
        element.style.removeProperty('transform');
      }
    });
  }, [isClosing]);
};
